import {
  Button,
  ButtonGroup,
  Dialog,
  DialogBody,
  DialogFooter,
  Icon,
  Intent,
  Menu,
  MenuItem,
  Popover,
  Tag,
  Tooltip,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import { useObserver } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DataTable, DataTableColumn, DropDown, SearchInput, SelectItem } from '@zaiusinc/hera';
import { LoadingIndicator } from '../../../components/LoadingIndicator';
import { showToasts } from '../../../lib/toaster';
import { SegmentAnalytics } from '../../../lib/SegmentAnalytics';
import { HostPageMessenger } from '../../../lib/HostPageMessenger';
import { showDataSyncMetricsUi } from '../../../lib/optimizelyfx';
import { DataSyncStore } from '../../../stores/DataSyncStore';
import { DataSyncMetricsStore } from '../../../stores/DataSyncMetricsStore';
import { MetricsGranularity } from '../../../lib/metricsUtils';
import { SortDirection } from '../../../types/Sort';
import { DataSyncItem, OptimizelyProduct, SyncStrategy } from '../../../types/DataSyncItem';
import { DataSyncHealthStatus } from '../../../types/DataSyncMetrics';
import { DataSyncStrategyProvider } from '../DataSyncStrategyProvider';
import DataSyncIcons from './DataSyncIcons';
import HealthIndicator from './HealthIndicator';
import SparklineChart from './SparklineChart';
import styles from './DataSyncList.module.sass';

const filterByOptions: SelectItem[] = [
  { text: 'All', value: 'all' },
  { text: 'Active', value: 'active' },
  { text: 'Paused', value: 'paused' },
];

const sortByOptions: SelectItem[] = [
  { text: 'Created Date', value: 'createdAt' },
  { text: 'Destination', value: 'destination.destinationObject.objectName' },
  { text: 'Destination Product', value: 'destination.productName' },
  { text: 'Last Sync', value: 'lastExecution.createdAt' },
  { text: 'Name', value: 'name' },
  { text: 'Source', value: 'source.sourceObject.objectName' },
  { text: 'Source Product', value: 'source.productName' },
  { text: 'Updated Date', value: 'updatedAt' },
];

const ANALYTICS_NEW_BADGE_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

const shouldShowAnalyticsNewBadge = (): boolean => {
  const releasedAt = process.env.REACT_APP_DATA_SYNC_ANALYTICS_RELEASED_AT;

  if (!releasedAt) {
    return true;
  }

  const releasedAtMs = Date.parse(releasedAt);
  if (Number.isNaN(releasedAtMs)) {
    return true;
  }

  return (Date.now() - releasedAtMs) < ANALYTICS_NEW_BADGE_DURATION_MS;
};

const formatDate = (value?: string): { date: string; time?: string } => {
  if (!value) {
    return { date: 'Real-time' };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: 'Real-time' };
  }

  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const timePart = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return { date: datePart, time: timePart };
};

const renderStatusTag = (data: DataSyncItem) => {
  const isActive = data.enabled || DataSyncStrategyProvider.getStrategy(data) === SyncStrategy.WEBHOOK;
  return (
    <Tag round={true} className={isActive ? styles.statusActive : styles.statusPaused}>
      {isActive ? 'Active' : 'Paused'}
    </Tag>
  );
};

const buildActivityLogsHashForLast7Days = () => {
  const nowEpoch = Math.floor(Date.now() / 1000);
  const startEpoch = nowEpoch - (7 * 24 * 60 * 60);
  return `/activity_logs?rowStart=1&timeFrame=days_7&start=${startEpoch}&end=${nowEpoch}&activity=data_sync_error`;
};

interface ListMetricsQuery {
  trackerId: string;
  fromEpoch: number;
  toEpoch: number;
  granularity: MetricsGranularity;
}

interface DataSyncListProps {
  dataSyncStore: DataSyncStore;
  metricsStore: DataSyncMetricsStore;
  metricsQuery?: ListMetricsQuery;
}

const DataSyncList: React.FC<DataSyncListProps> = ({ dataSyncStore, metricsStore, metricsQuery }) => {
  const history = useHistory();
  const analyticsNewBadgeVisible = shouldShowAnalyticsNewBadge();
  const [deleteModalData, setDeleteModalData] = useState<{ dataSync: DataSyncItem } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [canTriggerById, setCanTriggerById] = useState<Record<string, boolean>>({});
  const [checkingTriggerFor, setCheckingTriggerFor] = useState<string | null>(null);
  const pageInputRef = useRef<HTMLInputElement | null>(null);

  return useObserver(() => {
    const isDataSyncMetricsUiEnabled = showDataSyncMetricsUi();
    const total = dataSyncStore.paging.total;
    const rowCount = dataSyncStore.paging.rowCount;
    const rowStart = dataSyncStore.paging.rowStart;
    const rowEnd = Math.min(rowStart - 1 + rowCount, total);
    const totalPages = rowCount > 0 ? Math.max(1, Math.ceil(total / rowCount)) : 1;
    const currentPage = rowCount > 0 ? Math.max(1, Math.floor((rowStart - 1) / rowCount) + 1) : 1;

    const getVisiblePages = (): Array<number | 'ellipsis'> => {
      if (totalPages <= 9) {
        return Array.from({ length: totalPages }, (_, idx) => idx + 1);
      }

      const pages: Array<number | 'ellipsis'> = [1];

      let start = Math.max(2, currentPage - 2);
      let end = Math.min(totalPages - 1, currentPage + 2);

      if (currentPage <= 4) {
        start = 2;
        end = 6;
      }

      if (currentPage >= totalPages - 3) {
        start = totalPages - 5;
        end = totalPages - 1;
      }

      if (start > 2) {
        pages.push('ellipsis');
      }

      for (let page = start; page <= end; page += 1) {
        pages.push(page);
      }

      if (end < totalPages - 1) {
        pages.push('ellipsis');
      }

      pages.push(totalPages);
      return pages;
    };

    const goToPage = (targetPage: number) => {
      const page = Math.max(1, Math.min(totalPages, targetPage));
      const targetRowStart = ((page - 1) * rowCount) + 1;
      dataSyncStore.setPaging({
        ...dataSyncStore.paging,
        rowStart: targetRowStart,
      });
    };

    const goToTypedPage = () => {
      const typedPage = Number(pageInputRef.current?.value);
      if (!Number.isNaN(typedPage)) {
        goToPage(typedPage);
      }
    };

    const visiblePages = getVisiblePages();

    const shouldShowPauseStartMenu = (data: DataSyncItem) => {
      return DataSyncStrategyProvider.getStrategy(data) === SyncStrategy.SCHEDULED;
    };

    const renderEmptyState = () => (
      <div className={styles.emptyDataSyncList}>
        <Icon icon={IconNames.SEARCH} iconSize={64} />
        <p>No data syncs were found</p>
      </div>
    );

    const getDisabledTooltipText = (data: DataSyncItem) => {
      const isChecking = checkingTriggerFor === data.id;
      const canTrigger = canTriggerById[data.id] ?? true;

      if (isChecking) {
        return 'Checking if sync can be triggered...';
      }
      if (!canTrigger) {
        if (data.source.product === OptimizelyProduct.HUB_APP) {
          return 'No job configuration found for this data sync';
        }
        return 'Sync cannot be triggered at this time';
      }
      return '';
    };

    const handleToggleDataSync = async (dataSync: DataSyncItem) => {
      await SegmentAnalytics.track('Data Sync Action', {
        action: dataSync.enabled ? 'Pause' : 'Start',
        path: window.location.pathname + window.location.search,
        dataSyncId: dataSync.id,
      });
      try {
        await dataSyncStore.toggleDataSync(dataSync.id);
      } catch (error) {
        showToasts([{ intent: 'warning', message: `Cannot start/pause data sync. ${error}` }]);
      }
    };

    const handleDeleteDataSync = async (dataSync: DataSyncItem) => {
      if (isDeleting) {
        return;
      }
      setIsDeleting(true);
      await SegmentAnalytics.track('Data Sync Action', {
        action: 'Delete',
        path: window.location.pathname + window.location.search,
        dataSyncId: dataSync.id,
      });

      try {
        await dataSyncStore.deleteDataSync(dataSync.id);
        showToasts([{ intent: 'success', message: 'Data sync deleted successfully' }]);
      } catch (error) {
        showToasts([{ intent: 'warning', message: `Cannot delete data sync. ${error}` }]);
      } finally {
        setIsDeleting(false);
      }
    };

    const handleTriggerSync = async (dataSync: DataSyncItem) => {
      await SegmentAnalytics.track('Data Sync Action', {
        action: 'Trigger',
        path: window.location.pathname + window.location.search,
        dataSyncId: dataSync.id,
      });

      if (await dataSyncStore.triggerSync(dataSync)) {
        showToasts([{ intent: 'success', message: 'Sync triggered successfully' }]);
      } else {
        showToasts([{ intent: 'warning', message: 'Failed to trigger sync' }]);
      }
    };

    const renderMenu = (data: DataSyncItem) => {
      const isChecking = checkingTriggerFor === data.id;
      const canTrigger = canTriggerById[data.id] ?? true;

      return (
          <Menu className={styles.menu}>
            <MenuItem htmlTitle="Executions" text="Executions" onClick={() => history.push(`/data_syncs/${data.id}/executions`)} />
          <Tooltip content={getDisabledTooltipText(data)} disabled={!!canTrigger && !isChecking} fill={true}>
            <MenuItem
              text="Trigger now"
              icon={isChecking ? IconNames.REFRESH : undefined}
              disabled={!canTrigger || isChecking}
              onClick={() => handleTriggerSync(data)}
            />
          </Tooltip>
            {isDataSyncMetricsUiEnabled && (
              <MenuItem
                htmlTitle="Analytics"
                text="Analytics"
                labelElement={analyticsNewBadgeVisible ? <Tag minimal={true} round={true}>New</Tag> : undefined}
                onClick={() => history.push(`/data_syncs/${data.id}/analytics`)}
              />
            )}
          {shouldShowPauseStartMenu(data) && (
            <MenuItem
              htmlTitle="Pause/Start"
              text={data.enabled ? 'Pause' : 'Start'}
              onClick={() => handleToggleDataSync(data)}
            />
          )}
          <MenuItem htmlTitle="Edit" text="Edit" onClick={() => history.push(`/data_syncs/${data.id}`)} />
          <MenuItem htmlTitle="Delete" text="Delete" onClick={() => setDeleteModalData({ dataSync: data })} />
        </Menu>
      );
    };

    const renderDeleteDialog = () => {
      const isOpen = !!deleteModalData;
      if (!isOpen) {
        return null;
      }

      const closeDialog = () => {
        if (!isDeleting) {
          setDeleteModalData(null);
        }
      };

      return (
        <Dialog isOpen={isOpen} title="Delete Data Sync" onClose={closeDialog}>
          <DialogBody>
            <p>Are you sure you want to delete this data sync?</p>
          </DialogBody>
          <DialogFooter
            actions={
              <>
                <Button text="Cancel" onClick={closeDialog} disabled={isDeleting} />
                <Button
                  intent={Intent.DANGER}
                  text={isDeleting ? 'Deleting...' : 'Delete'}
                  loading={isDeleting}
                  disabled={isDeleting}
                  onClick={async () => {
                    if (deleteModalData?.dataSync && !isDeleting) {
                      await handleDeleteDataSync(deleteModalData.dataSync);
                      closeDialog();
                    }
                  }}
                />
              </>
            }
          />
        </Dialog>
      );
    };

    const rows = dataSyncStore.dataSyncs.map((data) => {
      const metrics = metricsQuery ? metricsStore.getSummary(data.id, metricsQuery) : undefined;
      const metricsLoading = metricsQuery ? metricsStore.isLoadingFor(data.id, metricsQuery) : false;
      const formattedLastSync = formatDate(data.lastExecution?.created_at);
      const sourceText = data.source.object.object_display_name || data.source.object.object_name;
      const destinationObject = data.destination.object.object_name || '-';
      const destinationInstance = data.destination.instance?.name ? `: ${data.destination.instance.name}` : '';

      return {
        id: data.id,
        data,
        metrics,
        // Show the sparkline skeleton only while we have no data to display yet;
        // once a summary lands, stale-while-revalidate keeps the chart visible.
        sparklineLoading: (metricsLoading && !metrics) || (!metricsQuery && !metrics),
        sourceText,
        destinationText: `${destinationObject}${destinationInstance}`,
        formattedLastSync,
        healthStatus: metrics?.healthStatus || DataSyncHealthStatus.HEALTHY,
      };
    });

    type DataSyncRow = typeof rows[number];

    const columns: DataTableColumn[] = [
      {
        id: 'name',
        title: 'Name',
        width: '30%',
        sortable: false,
        className: styles.nameCell,
        formatter: (_v: unknown, row: DataSyncRow) => (
          <div className={styles.nameWrap}>
            <DataSyncIcons dataSync={row.data} />
            <div className={styles.nameTextBlock}>
              <button
                className={styles.nameLink}
                onClick={() => history.push(`/data_syncs/${row.id}`)}
              >
                {row.data.name}
              </button>
            </div>
          </div>
        ),
      },
      {
        id: 'route',
        title: 'Source → Destination',
        width: '24%',
        sortable: false,
        className: styles.routeCell,
        formatter: (_v: unknown, row: DataSyncRow) => (
          <div className={styles.routeWrap}>
            <span>{row.sourceText}</span>
            <Icon icon={IconNames.CARET_RIGHT} className={styles.routeArrow} />
            <span>{row.destinationText}</span>
          </div>
        ),
      },
      {
        id: 'status',
        title: 'Status',
        width: '13%',
        sortable: false,
        className: styles.statusCell,
        formatter: (_v: unknown, row: DataSyncRow) => {
          const isWarning = row.healthStatus === DataSyncHealthStatus.WARNING;
          const isFailing = row.healthStatus === DataSyncHealthStatus.FAILING;
          return (
            <div className={styles.statusContent}>
              {renderStatusTag(row.data)}
              {(isWarning || isFailing) && (
                <HealthIndicator
                  status={row.healthStatus}
                  onClick={(event?: React.MouseEvent<HTMLButtonElement>) => {
                    event?.stopPropagation();
                    HostPageMessenger.send('setWindowLocation', {
                      hash: buildActivityLogsHashForLast7Days(),
                    });
                  }}
                />
              )}
            </div>
          );
        },
      },
      ...(isDataSyncMetricsUiEnabled
        ? [{
          id: 'successRate',
          title: 'Success rate (Last 7 days)',
          width: '21%',
          sortable: false,
          className: styles.successCell,
          formatter: (_v: unknown, row: DataSyncRow) => (
            <div className={styles.sparklineWrap}>
              <SparklineChart data={row.metrics?.sparklineData || []} loading={row.sparklineLoading} />
            </div>
          ),
        } as DataTableColumn]
        : []),
      {
        id: 'lastSync',
        title: 'Last sync',
        width: '9%',
        sortable: false,
        className: styles.lastSyncCell,
        formatter: (_v: unknown, row: DataSyncRow) => (
          <>
            <div>{row.formattedLastSync.date}</div>
            {row.formattedLastSync.time && (
              <div className={styles.lastSyncTime}>{row.formattedLastSync.time}</div>
            )}
          </>
        ),
      },
      {
        id: 'actions',
        title: '',
        width: '5%',
        sortable: false,
        className: styles.actionsCell,
        formatter: (_v: unknown, row: DataSyncRow) => (
          <Popover
            content={renderMenu(row.data)}
            placement="bottom-end"
            usePortal={false}
            popoverClassName={styles.popoverMenu}
            onOpening={async () => {
              setCheckingTriggerFor(row.data.id);
              try {
                const canTrigger = await dataSyncStore.canTriggerSync(row.data);
                setCanTriggerById((prev) => ({ ...prev, [row.data.id]: canTrigger }));
              } catch {
                setCanTriggerById((prev) => ({ ...prev, [row.data.id]: false }));
              } finally {
                setCheckingTriggerFor(null);
              }
            }}
          >
            <Button
              small={true}
              minimal={true}
              icon={IconNames.MORE}
              className={styles.ellipsisButton}
            />
          </Popover>
        ),
      },
    ];

    return (
      <div className={styles.wrapper}>
        <div className={styles.listTop}>
          <SearchInput
            className={styles.searchInput}
            onChange={(value) => {
              dataSyncStore.setSearchFilter(value);
            }}
            placeholder="Search..."
            searchTerm={dataSyncStore.searchFilter}
          />
          <div className={styles.filtersSection}>
            <span className={styles.filter}>Filter</span>
            <DropDown
              items={filterByOptions}
              value={dataSyncStore.filterByStatus}
              onItemSelect={(status) => dataSyncStore.setFilterByStatus(status.value)}
            />
            <span className={styles.filter}>Sort by</span>
            <ButtonGroup>
              <Button
                icon={dataSyncStore.sort.direction === SortDirection.DESC ? 'sort-desc' : 'sort-asc'}
                onClick={() => {
                  const direction =
                    dataSyncStore.sort.direction === SortDirection.DESC
                      ? SortDirection.ASC
                      : SortDirection.DESC;
                  dataSyncStore.setSortDirection(direction);
                }}
              />
              <DropDown
                items={sortByOptions}
                initialContent="Last Sync"
                value={dataSyncStore.sort.field}
                onItemSelect={(status) => dataSyncStore.setSortField(status.value)}
              />
            </ButtonGroup>
          </div>
        </div>

        {renderDeleteDialog()}

        <div className={styles.listContent}>
          {rows.length === 0 && !dataSyncStore.loading ? (
            renderEmptyState()
          ) : (
            <>
              <div
                className={classNames(styles.tableViewport, {
                  [styles.tableViewportLoading]: dataSyncStore.loading,
                })}
              >
                {dataSyncStore.loading && (
                  <div className={styles.tableLoadingOverlay}>
                    <LoadingIndicator className={styles.tableLoadingIndicator} size={34} />
                  </div>
                )}
                <div className={styles.dataSyncTable}>
                  <DataTable
                    columns={columns}
                    data={rows}
                    loading={false}
                    hideTop={true}
                    hideBottom={true}
                    hideBorder={true}
                    rowStart={1}
                    rowCount={rows.length}
                    total={total}
                    sortColumn=""
                    sortAsc={true}
                    onPaging={() => { /* unused — pagination is rendered separately below */ }}
                    onSorting={() => { /* unused — sortable: false on every column */ }}
                    // Use Hera's <ScrollableContainer> so each <th> gets the
                    // `stickyHeader` class (pseudo-element borders that travel
                    // with sticky positioning, plus scroll-shadow gradients).
                    scrollableContainerProps={{}}
                  />
                </div>
              </div>

              <div className={styles.paginationRow}>
                <span className={styles.pagingLabel}>
                  {total > 0 ? `${rowStart}-${rowEnd} of ${total}` : '0-0 of 0'}
                </span>

                <div className={styles.pagingButtons}>
                  <Button
                    icon={IconNames.CHEVRON_LEFT}
                    minimal={true}
                    small={true}
                    disabled={currentPage <= 1}
                    onClick={() => goToPage(currentPage - 1)}
                  />

                  {visiblePages.map((page, index) =>
                    page === 'ellipsis' ? (
                      <span key={`ellipsis-${index}`} className={styles.ellipsisToken}>...</span>
                    ) : (
                      <Button
                        key={`page-${page}`}
                        small={true}
                        minimal={page !== currentPage}
                        className={classNames(styles.pageNumberButton, {
                          [styles.pageNumberButtonActive]: page === currentPage,
                        })}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </Button>
                    )
                  )}

                  <Button
                    icon={IconNames.CHEVRON_RIGHT}
                    minimal={true}
                    small={true}
                    disabled={currentPage >= totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                  />
                </div>

                <div className={styles.pageJumpGroup}>
                  <span className={styles.pageJumpLabel}>Page</span>
                  <input
                    key={`page-input-${currentPage}`}
                    className="bp5-input"
                    type="number"
                    min={1}
                    max={totalPages}
                    defaultValue={currentPage}
                    ref={pageInputRef}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        goToTypedPage();
                      }
                    }}
                  />
                  <Button
                    small={true}
                    text="Go"
                    onClick={goToTypedPage}
                  />
                  <span className={styles.pageJumpMeta}>of {totalPages}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  });
};

export default DataSyncList;
